<template>
  <div
    v-if="asset"
    class="block-tile-container"
  >
    <template v-if="featuredImage && featuredImage.largeThumbnail">
      <template v-if="isAttachment">
        <div class="image-container">
          <img
            class="featured-image"
            :src="featuredImage.largeThumbnail"
            :alt="`Featured image for ${title}`"
          >
          <button
            class="overlay"
            @click="$emit('tile-clicked')"
          >
            <bookmark-button
              v-if="showBookmark"
              class="bookmark-button"
              :asset-name="title"
              :bookmarkable-type="assetType"
              :bookmarkable-id="asset && asset.id"
              :source="source"
              icon-variant="white"
            />
            <div class="play" />
          </button>
          <attachment-progress
            v-if="state"
            :duration="asset.metadata && asset.metadata.duration"
            :watched="state.currentTimestamp"
          />
        </div>
      </template>
      <template v-else>
        <button
          class="image-container zoom"
          @click="$emit('tile-clicked')"
        >
          <img
            class="featured-image"
            :src="featuredImage.largeThumbnail"
            :alt="`Featured image for ${title}`"
          >
          <div
            v-if="showBookmark && assetIsBookmarkable"
            class="bookmark-button-backdrop"
          >
            <bookmark-button
              :asset-name="title"
              :bookmarkable-type="assetType"
              :bookmarkable-id="asset && asset.id"
              :source="source"
              icon-variant="white"
            />
          </div>
          <div
            v-if="assetInProgress || assetCompleted"
            class="asset-state-action"
          >
            <icon
              v-if="assetCompleted"
              name="check"
              variant="white"
              class="completed-icon"
            />
            {{ progressText }}
          </div>
        </button>
      </template>
    </template>
    <button
      v-else
      class="image-container fallback"
      @click="$emit('tile-clicked')"
    >
      <icon
        :name="metadataIconName"
        variant="content-secondary"
        class="fallback-icon"
      />
      <attachment-progress
        v-if="state"
        :duration="asset.metadata && asset.metadata.duration"
        :watched="state.currentTimestamp"
      />
    </button>
    <asset-metadata
      :asset="asset"
      :icon-variant="'content-secondary'"
      :tile="true"
    />
    <button
      class="card-title"
      @click="$emit('tile-clicked')"
    >
      <label-text :size="'lg'">
        {{ title }}
      </label-text>
    </button>
    <hlt-text
      v-if="descriptionText"
      class="asset-description"
      :size="'sm'"
    >
      {{ descriptionText }}
    </hlt-text>
    <content-source-preview :content-source="contentSource" />
  </div>
</template>

<script>
import AssetMetadata from '@components/ui/asset-metadata.vue';
import AttachmentProgress from '@components/ui/attachment-progress.vue';
import BookmarkButton from '@components/singletons/bookmarkButton.vue';
import ContentSourcePreview from '@components/content-sources/preview.vue';
import LabelText from '@components/ui/label-text.vue';
import HltText from '@components/ui/text.vue';
import Icon from '@components/singletons/icon.vue';

export default {
  components: {
    AssetMetadata,
    AttachmentProgress,
    BookmarkButton,
    ContentSourcePreview,
    LabelText,
    HltText,
    Icon,
  },
  props: {
    asset: {
      type: Object,
    },
    assetCompleted: {
      type: Boolean,
    },
    assetInProgress: {
      type: Boolean,
    },
    assetType: {
      type: String,
    },
    contentSource: {
      type: Object,
    },
    featuredImage: {
      type: Object,
    },
    metadata: {
      type: String,
    },
    metadataIconName: {
      type: String,
    },
    showBookmark: {
      type: Boolean,
      default: false,
    },
    // source is primarily used for the bookmarks analytics events. If any changes are make,
    // make sure that they match the expected values for those events.
    source: {
      type: Object,
      default: () => ({}),
    },
    state: {
      type: [Array, Object],
    },
    title: {
      type: String,
    },
  },
  computed: {
    assetIsBookmarkable() {
      const bookmarkableAssetTypes = ['Attachment', 'Topic'];
      return bookmarkableAssetTypes.includes(this.assetType);
    },
    descriptionText() {
      return this.asset?.assetDescription;
    },
    isAttachment() {
      return this.assetType === 'Attachment';
    },
    isDeck() {
      return this.assetType === 'Deck';
    },
    isGrouping() {
      return this.assetType === 'Grouping';
    },
    isLearningModule() {
      return this.assetType === 'LearningModule';
    },
    progressText() {
      if (this.assetCompleted) {
        return 'Complete';
      } else if (this.assetInProgress) {
        return 'Continue';
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.block-tile-container {
  display: inline-block;
  // image + 24 for metadata + 40 for title
  margin-right: rem-calc(8);
  width: rem-calc(343);
  overflow: hidden;
  vertical-align: top;

  .image-container {
    @include zoom-widget;
    border-radius: 4px;
    border: 1px solid #cccccc;
    box-sizing: border-box;
    display: block;
    height: rem-calc(193.7); // 16/9 ratio
    object-fit: cover;
    object-position: center;
    overflow: hidden;
    padding: 0;
    position: relative;
    width: 100%;

    &:hover {
      .overlay,
      .bookmark-button-backdrop {
        background: rgba(36, 34, 34, .6);
        display: flex;
      }

      ~ .card-title {
        text-decoration: underline;
      }
    }

    .featured-image {
      object-fit: cover;
      object-position: center;
      height: 100%;
      width: 100%;
    }

    .overlay {
      @include widget-overlay;

      .bookmark-button {
        position: absolute;
        right: 12px;
        top: 12px;
      }
    }

    .bookmark-button-backdrop {
      align-items: center;
      border-radius: 4px;
      display: none;
      justify-content: center;
      padding: 4px;
      position: absolute;
      right: 8px;
      top: 8px;
    }

    &.fallback {
      align-items: center;
      background: #ffffff;
      display: flex;
      justify-content: center;

      .fallback-icon {
        width: rem-calc(32);
      }
    }
  }

  .asset-state-action {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background-color: #000000;
    color: #ffffff;
    text-align: center;
    font-weight: $bold;
    padding: rem-calc(6) 0;
    font-size: rem-calc(10);

    .completed-icon {
      width: rem-calc(10);
      height: rem-calc(10);
      padding: 2px;
      border-radius: 100%;
      background-color: #00b46d;
      vertical-align: middle;
      margin-right: rem-calc(4);
    }
  }

  .card-title {
    background: none;
    margin-top: rem-calc(12);
    overflow: hidden;
    padding: 0;
    text-align: left;
    white-space: normal;
    width: 100%;

    &:hover {
      text-decoration: underline;
    }

    /deep/ p {
      display: -webkit-box;
      // height: rem-calc(48);
      margin: 0;
      text-overflow: ellipsis;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
    }
  }

  .asset-description {
    display: -webkit-box;
    margin-top: rem-calc(8);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: normal;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
  }
}
</style>
